import set from 'lodash/set';
import _merge from 'lodash/merge';
import { toJS, transaction as mobxTransaction } from 'mobx';
import cloneDeep from 'lodash/cloneDeep';
import { transaction } from '@src/slide/domain/slide-app';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { AutoFitType, WrapType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import { isShapeAutoFit, getTargetSizeAfterHeightChange, getTargetSizeAfterWidthChange } from '@src/slide/domain/ui-model/element-modeles/property/text/text-adjust/utils';
import { getDefaultTextRect } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/text-rect';
import GroupModel from '@src/slide/domain/ui-model/element-modeles/group-model';
import { ShapeType } from '@src/slide/infrastructure/module/dependent/shape-info/graphic-type';
import { ITextAdjustOptions, IElementFrame } from '@src/slide/domain/ui-model/element-modeles/property/text/text-adjust/text-adjust.interface';
import { isVerticalDirection, getLogicPropertyKeyMap } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/text-direction';
import ResizeMeasureCanvas from '@src/slide/domain/ui-model/element-modeles/property/text/editor-measure/resize-measure';
import { EditorDataModel } from '@src/slide/domain/ui-model/element-modeles/property/text/editor-data-model';
import { MODES } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import { IShapeModel } from '../../../Interface/shape-model.interface';
import { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';

const SpecialShape = [
    ShapeType.leftUpArrow,
    ShapeType.leftRightUpArrow,
    ShapeType.quadArrow,
    ShapeType.bentUpArrow,
    ShapeType.mathPlus,
];

/**
 * 影响形状大小的值
 * 1. 文字属性，字体字号内容等等，都包含在 model.paragraphList 中
 * 2. 形状边距、自缩排属性等最外层的文本属性，包含在 model.textBodyStyle 中
 */
export type ShapeSizeDependentValues = {
    textBodyStyle?: IShapeModel['textBodyStyle'],
    paragraphList?: IShapeModel['paragraphList'],
};

export default class TextAdjustManager {
    private static instance;
    public static getInstance(): TextAdjustManager {
        if (!TextAdjustManager.instance) {
            TextAdjustManager.instance = new TextAdjustManager();
        }
        return TextAdjustManager.instance;
    }

    public adjustedModelFrameMap = new Map();
    private shapeToGroupInfoMap = new Map();
    private textBlockSizeMap = new Map();
    private displayBlockSizeMap = new Map();

    // 开关
    public enableAdjustText = (): boolean => true;

    /**
     * 缩放形状时重新调整形状高度
     * 横排文本：纵向缩放，形状高度保持不变； 横向缩放，根据缩放后宽度重新计算出形状高度
     * 竖排文本：纵向缩放，根据缩放后宽度重新计算出形状高度；横向缩放，形状高度保持不变
     * @param element
     * @param frame
     * @param mode
     */
    public adjustShape = (element: IShapeModel, frame: IElementFrame, mode: string): IElementFrame => {
        const textDirection = element?.textBodyStyle?.wrapperStyle?.textDirection;
        const isVert = isVerticalDirection(textDirection);
        const textRect = getDefaultTextRect(element, frame.style);

        // 根据缩放后的文本逻辑宽度计算出文本逻辑高度
        const textBody = EditorDataModel.convertFromTextModel({
            paragraphList: element.paragraphList as [],
            textBodyStyle: element.textBodyStyle,
        }, { noConvertBack: true });
        const textBlockSize = this.getTextBlockSize(
            textRect, element,
            textBody, element.textBodyStyle, frame.style,
        );
        this.textBlockSizeMap.set(element.key, textBlockSize);

        const displayBlockSize = this.getDisplayBlockSize(element, textBlockSize, textRect, frame.style);

        let targetSize = frame.style;
        if (!isVert && frame.style.height !== displayBlockSize) {
            targetSize = getTargetSizeAfterHeightChange(element, frame.style, displayBlockSize, mode);
        } else if (isVert && frame.style.width !== displayBlockSize) {
            targetSize = getTargetSizeAfterWidthChange(element, frame.style, displayBlockSize, mode, textDirection);
        }

        return {
            style: {
                ...frame.style,
                width: targetSize.width,
                height: targetSize.height,
                left: targetSize.left,
                top: targetSize.top,
            },
        };
    };

    /**
     * 形状设置了跟随文本大小自动调整，根据文本宽高调整形状大小
     * 如果没传 shapeSizeDependentValues，默认按照传入的 element 当前数据来计算
     */
    public calculateAndUpdateShapeStyle(element, update, shapeSizeDependentValues: ShapeSizeDependentValues = {}) {
        // 获取到最新的完整的 textBodyStyle
        const fullTextBodyStyle = _merge({}, element.textBodyStyle, shapeSizeDependentValues.textBodyStyle);
        if (fullTextBodyStyle?.autoFitType !== AutoFitType.SHAPE_AUTO_FIT) {
            return;
        }
        const { paragraphList = element.paragraphList } = shapeSizeDependentValues;

        if (element.textBodyStyle?.autoFitType !== AutoFitType.SHAPE_AUTO_FIT) {
            // 元素格式不是 spAutoFit，清除掉缓存
            this.textBlockSizeMap.delete(element.key);
        }

        const { displayInlineSize, textBlockSize } = this
            .getInlineAndBlockSize(element, fullTextBodyStyle, paragraphList);

        if (update) {
            this.updateShapeStyle(element, textBlockSize, displayInlineSize, fullTextBodyStyle);
        } else {
            this.updateShapeStyleForUI(element, textBlockSize, displayInlineSize);
        }
    }

    /**
     * 提供给格式刷，用于更新形状大小和位置
     * @param element
     * @param srcProps
     * @param options
     */
    public updateElementProps = (
        element: IShapeModel,
        srcProps: { style: any },
        options: ITextAdjustOptions,
    ) => {
        const { textBodyStyle, paragraphList } = options;
        if (textBodyStyle?.autoFitType !== AutoFitType.SHAPE_AUTO_FIT) {
            return;
        }
        const { displayInlineSize, textBlockSize } = this.getInlineAndBlockSize(element, textBodyStyle, paragraphList);
        const textRect = getDefaultTextRect(element);

        const displayBlockSize = this.getDisplayBlockSize(element, textBlockSize, textRect, undefined, textBodyStyle);

        const { style } = this.getAdjustInfos(element, displayBlockSize, displayInlineSize, textBodyStyle);

        const needSetProps = ['width', 'height', 'top', 'left'];
        needSetProps.forEach((item) => {
            if (style[item]) {
                srcProps.style[item] = style[item];
            }
        });
    };

    /**
     * 根据文本宽高更新形状宽高
     * @param element
     * @param textHeight
     * @param displayWidth
     */
    public updateShapeStyle = (
        element: IShapeModel,
        textBlockSize: number,
        displayInlineSize: number,
        textBodyStyle?,
    ): void => {
        if (!this.enableAdjustText()) return;

        const textRect = getDefaultTextRect(element);
        const displayBlockSize = this.getDisplayBlockSize(element, textBlockSize, textRect, null, textBodyStyle);
        const { style } = this.getAdjustInfos(element, displayBlockSize, displayInlineSize, textBodyStyle);
        const props = {
            style: {
                width: Math.ceil(style.width),  // 处理小数精度问题
                height: Math.ceil(style.height),
                left: style.left,
                top: style.top,
            },
        };
        this.adjustedModelFrameMap.set(element.key, { ...props.style });
        transaction(() => {
            element.updateProps(props);
            this.updateConnectorInfo(element);

            const isGroupChildren = element?.parent?.type === UIModelType.GROUP;
            if (isGroupChildren) {
                this.updateGroupStyle(element, props.style);
            }
        });
    };

    /**
     * 根据文本宽高更新形状显示宽高，不提交数据
     * @param element
     * @param textHeight
     * @param displayWidth
     */
    public updateShapeStyleForUI = (element: IShapeModel, textBlockSize: number, displayInlineSize: number) => {
        if (!this.enableAdjustText()) return;
        const updateShapeStyleForUIInner = () => {
            const textDirection = element?.textBodyStyle?.wrapperStyle?.textDirection;
            const logicKyes = getLogicPropertyKeyMap(textDirection);
            const textRect = getDefaultTextRect(element);
            const elementBlockSize = element.props.style[logicKyes.blockSize];
            const displayBlockSize = (
                this.textBlockSizeMap.get(element.key) === textBlockSize
                && Math.abs(this.displayBlockSizeMap.get(element.key) - elementBlockSize) < 1
            )
                ? elementBlockSize
                : this.getDisplayBlockSize(element, textBlockSize, textRect);
            this.textBlockSizeMap.set(element.key, textBlockSize);
            this.displayBlockSizeMap.set(element.key, displayBlockSize);

            const { offsetFrame, style } = this.getAdjustInfos(element, displayBlockSize, displayInlineSize);
            this.updateElementStyle(element, style, 'operationFrame');
            this.updateElementStyle(element, style, 'props.style');

            const isGroupChildren = element?.parent?.type === UIModelType.GROUP;
            if (isGroupChildren) {
                this.updateGroupStyleForUI(element);
            }
            set(element, 'operationFrame', null);

            element?.relatedUIModelRefs?.forEach((relatedUIModel) => {
                const updateSytle = relatedUIModel.connectorCaculater.getUpdateStyle(offsetFrame);
                relatedUIModel.updateModelStyle(updateSytle);
            });
        };
        mobxTransaction(() => {
            updateShapeStyleForUIInner();
        });
    };

    /**
     * 元素的自适应方式为"根据文字调整形状大小"，在缩放时将换行方式设置为自动换行
     * @param element
     * @param mode
     */
    public updateElementWrapType(element: IShapeModel, mode): void {
        // 横排：从左侧或者右侧缩放时，将换行方式设置为自动换行
        // 竖排：从上侧或者下侧缩放时，将换行方式设置为自动换行
        const isVert = isVerticalDirection(element?.textBodyStyle?.wrapperStyle?.textDirection);
        let needBreak;
        if (isVert) {
            needBreak = mode?.indexOf(MODES.TOP) !== -1 || mode?.indexOf(MODES.BOTTOM) !== -1;
        } else {
            needBreak = mode?.indexOf(MODES.RIGHT) !== -1 || mode?.indexOf(MODES.LEFT) !== -1;
        }
        const isNoWrap = element?.textBodyStyle?.bodyStyle?.whiteSpace === WrapType.NO_WRAP;
        if (isShapeAutoFit(element) && isNoWrap && needBreak && element.textBodyStyle.bodyStyle) {
            element.textBodyStyle.bodyStyle.whiteSpace = WrapType.WRAP;

            element.updateParagraphList(element.paragraphList, element.textBodyStyle);
        }
    }

    public getDisplayInlineSize(element: IShapeModel, textInlineSize, textRect = {}, frame?, textDirection?) {
        const logicKey = getLogicPropertyKeyMap(textDirection || element?.textBodyStyle?.wrapperStyle?.textDirection);

        let elementInlineSize = frame?.[logicKey.inlineSize] || element.props.style[logicKey.inlineSize];
        const inlineEnd = elementInlineSize - textRect[logicKey.inlineStart] ?? 0 - textRect[logicKey.inlineSize] ?? 0;
        let displayInlineSize = textInlineSize + textRect[logicKey.inlineStart] ?? 0 + inlineEnd;

        let count = 0;
        while (Math.abs(displayInlineSize - elementInlineSize) > 1e-3 && count < 10) {
            elementInlineSize = displayInlineSize;
            const style = {
                [logicKey.inlineSize]: displayInlineSize,
                [logicKey.blockSize]: frame?.[logicKey.blockSize] || element.props.style[logicKey.blockSize],
            };

            const newTextRect = getDefaultTextRect(element, style);
            const newInlineEnd = displayInlineSize - newTextRect[logicKey.inlineStart]
            - newTextRect[logicKey.inlineSize];
            displayInlineSize = textInlineSize + newTextRect[logicKey.inlineStart] + newInlineEnd;
            count += 1;
        }

        return displayInlineSize;
    }

    /**
     * 清除当前 shape 缓存的组合信息
     * @param shape
     */
    public removeShapeToGroupInfoMap(shape: IShapeModel) {
        this.shapeToGroupInfoMap.delete(shape.key);
    }

    // 根据逻辑推测看起来是获取形状的逻辑宽度和文字的逻辑高度
    private getInlineAndBlockSize(element, textBodyStyle, paragraphList = element.paragraphList) {
        const textDirection = textBodyStyle?.wrapperStyle?.textDirection;
        const logicKeys = getLogicPropertyKeyMap(textDirection);

        // 获取当前形状文字区域大小
        const textRect = getDefaultTextRect(element);

        // 获取当前形状文字区域逻辑宽度
        const inlineSize = textRect ? textRect[logicKeys.inlineSize] : element.props.style[logicKeys.inlineSize];

        const cloneParaList = cloneDeep(paragraphList);

        const textBody = EditorDataModel.convertFromTextModel(
            { paragraphList: cloneParaList, textBodyStyle },
            { noConvertBack: true },
        );

        // 如果文字不可换行，那么当前形状文字区域逻辑宽度就是文字的实际宽度
        const textInlineSize = textBodyStyle?.bodyStyle?.whiteSpace !== WrapType.NO_WRAP ? inlineSize
            : ResizeMeasureCanvas.getEditorInlineSize(textBody, { textRect, textBodyStyle });

        // 获取形状的逻辑宽度
        const displayInlineSize = this.getDisplayInlineSize(
            element, textInlineSize, textRect, null,
            textBodyStyle?.wrapperStyle?.textDirection,
        );

        // 根据逻辑推测看起来是获取文字的逻辑高度
        const textBlockSize = this.getTextBlockSize(textRect, element, textBody, textBodyStyle);
        return { displayInlineSize, textBlockSize };
    }

    // eslint-disable-next-line complexity
    private getTextBlockSize(textRect, element: IShapeModel, textBody, textBodyStyle, frame?) {
        // 测量文字的逻辑高度
        let textBlockSize =  ResizeMeasureCanvas.getEditorBlockSize(textBody, { textBodyStyle, textRect });
        // 元素高度变化控制点可能会变化，文本宽度会跟随变化，这时候需要重新计算一下文本高度
        const elementHeight = frame?.height || element.props.style.height;
        const elementWidth = frame?.width || element.props.style.width;
        const bottom = elementHeight - textRect.height - textRect.top;
        const right = elementWidth - textRect.width - textRect.left;

        const textDirection = textBodyStyle?.wrapperStyle?.textDirection;
        const isVert = isVerticalDirection(textDirection);

        const style = isVert ? {
            height: frame?.height || element.props.style.height,
            width: textBlockSize + textRect.left + right,
        } : {
            height: textBlockSize + textRect.top + bottom,
            width: frame?.width || element.props.style.width,
        };

        const newTextRect = getDefaultTextRect(element, style);
        if (!isVert && newTextRect.width !== textRect.width) {
            textBlockSize = ResizeMeasureCanvas.getEditorBlockSize(textBody, {
                textBodyStyle,
                textRect: { ...textRect, width: newTextRect.width },
            });
        } else if (isVert && newTextRect.height !== textRect.height) {
            textBlockSize = ResizeMeasureCanvas.getEditorBlockSize(textBody, {
                textBodyStyle,
                textRect: { ...textRect, height: newTextRect.height },
            });
        }

        return textBlockSize;
    }

    private getAdjustInfos(element: IShapeModel, displayBlockSize: number, displayInlineSize: number, textBodyStyle?) {
        const { style } = element.props;
        let targetSize = style;
        const textDirection = textBodyStyle?.wrapperStyle
            ? textBodyStyle?.wrapperStyle?.textDirection
            : element?.textBodyStyle?.wrapperStyle?.textDirection;
        const isVert = isVerticalDirection(textDirection);
        const displayWidth = isVert ? displayBlockSize : displayInlineSize;
        const displayHeight = isVert ? displayInlineSize : displayBlockSize;
        if (Math.abs(style.height - displayHeight) >= 1) {  // 有可能有精度损失，暂时判断小于1
            targetSize = getTargetSizeAfterHeightChange(element, targetSize, displayHeight, undefined, textDirection);
        }
        if (Math.abs(style.width - displayWidth) >= 1) {
            targetSize = getTargetSizeAfterWidthChange(element, targetSize, displayWidth, undefined, textDirection);
        }
        const offsetFrame = {
            height: targetSize.height - style.height,
            width: targetSize.width - style.width,
            left: targetSize.left - style.left,
            top: targetSize.top - style.top,
        };

        return { offsetFrame, style: targetSize };
    }

    /**
     * 更新父元素样式
     * @param element
     */
    private updateGroupStyle(element: IShapeModel, style?) {
        let info;
        if (this.shapeToGroupInfoMap.has(element.key)) {
            info = this.shapeToGroupInfoMap.get(element.key);
        } else {
            this.updateElementStyle(element, style, 'operationFrame');
            this.updateElementStyle(element, style, 'props.style');
            // @ts-ignore
            info = (element.rootGroupModel as IGroupModel)?.getFrame().getAdjustedElementsAndProps([element]) ?? [];
            set(element, 'operationFrame', null);
        }
        if (info?.length > 0) {
            info.forEach((item) => {
                const { uiModel, props } = item;
                uiModel.updateAndRemoveProps(props);
            });
        }

        // @ts-ignore
        element.rootGroupModel?.getFrame().clearTempInfosWithElement(element);
    }

    /**
     * 更新父元素样式，不提交数据
     * @param element
     */
    private updateGroupStyleForUI(element: IShapeModel) {
        mobxTransaction(() => {
            const info = (element.rootGroupModel as IGroupModel)?.getFrame().getAdjustedElementsAndProps() ?? [];
            set(element, 'operationFrame', null);

            if (info?.length > 0) {
                info.forEach((item) => {
                    const { uiModel, props } = item;
                    this.updateElementStyle(uiModel as IShapeModel, props.style, 'props.style');
                    if (props.childFrame) {
                        this.updateElementStyle(uiModel as IShapeModel, props.childFrame, 'props.childFrame');
                    }
                    if (uiModel.type === UIModelType.GROUP) {
                        this.resetFrameRecursion(uiModel as GroupModel, element);
                    }
                });
                this.shapeToGroupInfoMap.set(element.key, info);
            }
        });
    }

    /**
     * 更新 element 关联的连接线信息
     * @param element
     */
    private updateConnectorInfo(element: IShapeModel) {
        element?.relatedUIModelRefs?.forEach((relatedUIModel) => {
            if (relatedUIModel.type === UIModelType.CONNECTOR) {
                const updateSytle = relatedUIModel.getUpdateStyleProps(this.adjustedModelFrameMap);
                const props = {
                    style: {
                        ...updateSytle.frame,
                        bgLine: updateSytle.bgLine,
                        controlValues: toJS(updateSytle.controlValues),
                    },
                };
                relatedUIModel.updateAndRemoveProps(props);
            }
        });
    }

    private updateElementStyle(element: IShapeModel, style, path) {
        // 需用mobxTransaction，避免更新operationFrame时触发无必要的渲染
        mobxTransaction(() => {
            set(element, `${path}.top`, style.top);
            set(element, `${path}.left`, style.left);
            set(element, `${path}.width`, style.width);
            set(element, `${path}.height`, style.height);
        });
    }

    private resetFrameRecursion = (group: GroupModel, element: IShapeModel) => {
        group?.children?.forEach((item) => {
            if (item.type === UIModelType.GROUP) {
                this.resetFrameRecursion(item, element);
            }
            const useProps = (item === element);
            item?.resetFrame(useProps);
        });
    };


    /**
     * 根据缩放后计算得到的文本块级尺寸 以及 textRect.top/right 循环计算得到合适元素块级尺寸
     * @param element
     * @param textHeight
     * @param textRect
     */
    private getDisplayBlockSize(element: IShapeModel, textBlockSize, textRect, frame?, textBodyStyle?) {
        const textDirection = textBodyStyle?.wrapperStyle
            ? textBodyStyle?.wrapperStyle?.textDirection
            : element?.textBodyStyle?.wrapperStyle?.textDirection;
        const logicKey = getLogicPropertyKeyMap(textDirection);

        let elementBlockSize = frame?.[logicKey.blockSize] || element.props.style[logicKey.blockSize];
        const blockEnd = elementBlockSize - textRect[logicKey.blockStart] - textRect[logicKey.blockSize];
        let displayBlockSize = textBlockSize + textRect[logicKey.blockStart] + blockEnd;
        if (element.props.style?.bgShape && SpecialShape.includes(element.props.style.bgShape)) {
            // 部分元素通过文本高度逆向形状高度，高度无法收敛，做特殊处理
            const text = this.textBlockSizeMap.get(element.key) || textBlockSize;
            return element.props.style[logicKey.blockSize] + textBlockSize - text;
        }
        let count = 0;
        while (Math.abs(displayBlockSize - elementBlockSize) > 1e-3 && count < 10) {
            elementBlockSize = displayBlockSize;
            const style = {
                [logicKey.inlineSize]: frame?.[logicKey.inlineSize] || element.props.style[logicKey.inlineSize],
                [logicKey.blockSize]: displayBlockSize,
            };

            const newTextRect = getDefaultTextRect(element, style);
            const newBlockEnd = displayBlockSize - newTextRect[logicKey.blockStart] - newTextRect[logicKey.blockSize];
            displayBlockSize = textBlockSize + newTextRect[logicKey.blockStart] + newBlockEnd;
            count += 1;
        }

        return displayBlockSize;
    }
}
